/**
 * DSN Now, ported from loader-dsn.js: /api/loader/dsn-now polled every 10 s
 * with backoff 30 s, 1 min, 2 min, 5 min on consecutive failures; the
 * spacecraft and dish maps; per-dish band tags, data rate, uplink power, range,
 * round-trip light time, az/el and wind; the "sim time differs from real time"
 * banner when the sim clock is more than 1 h from real time, in which case no
 * live contact lines are drawn. The runtime merges the live pairs with the
 * store's own active contacts and calls engine.setActiveContacts.
 */
import { loaderApi } from '../data/api';
import { useAwareness, type DsnDish, type DsnNowData, type DsnSignal, type DsnStation } from './store';
import { errorMessage } from './util';

export const DSN_POLL_MS = 10_000;
export const DSN_BACKOFF_STEPS_MS = [30_000, 60_000, 120_000, 300_000];
/** Contact lines (and the banner) key off this distance between sim and real time. */
export const DSN_REAL_TIME_TOLERANCE_MS = 3_600_000;

export interface SpacecraftInfo {
  /** Our mission id, null for spacecraft the loader does not model. */
  id: string | null;
  name: string;
  color: string;
}

/** DSN Now spacecraft codes to loader missions. */
export const SPACECRAFT_MAP: Record<string, SpacecraftInfo> = {
  EM2: { id: 'artemis2_orion', name: 'Artemis II / Orion', color: '#FF9800' },
  ART2: { id: 'artemis2_orion', name: 'Artemis II / Orion', color: '#FF9800' },
  MRO: { id: 'mro', name: 'Mars Recon Orbiter', color: '#2196F3' },
  MVN: { id: 'maven', name: 'MAVEN', color: '#9C27B0' },
  ODY: { id: 'odyssey', name: 'Mars Odyssey', color: '#FF5722' },
  MEX: { id: 'mars_express', name: 'Mars Express', color: '#00BCD4' },
  M20: { id: 'perseverance', name: 'Perseverance', color: '#FF9800' },
  MSL: { id: 'curiosity', name: 'Curiosity', color: '#4CAF50' },
  EURC: { id: null, name: 'Europa Clipper', color: '#7C4DFF' },
  SOHO: { id: null, name: 'SOHO', color: '#FFD600' },
  JWST: { id: null, name: 'James Webb', color: '#E91E63' },
  VGR1: { id: null, name: 'Voyager 1', color: '#607D8B' },
  VGR2: { id: null, name: 'Voyager 2', color: '#607D8B' },
  NHPC: { id: null, name: 'New Horizons', color: '#795548' },
  TGO: { id: null, name: 'Trace Gas Orbiter', color: '#009688' },
  CGO: { id: null, name: "Chang'e", color: '#F44336' },
  IMAP: { id: null, name: 'IMAP', color: '#3F51B5' },
};

/** DSN dish names to loader antenna ids. */
export const DSN_DISH_MAP: Record<string, string> = {
  DSS14: 'goldstone_70m',
  DSS24: 'goldstone_34m_1',
  DSS25: 'goldstone_34m_2',
  DSS26: 'goldstone_34m_3',
  DSS63: 'madrid_70m',
  DSS54: 'madrid_34m_1',
  DSS55: 'madrid_34m_2',
  DSS56: 'madrid_34m_3',
  DSS43: 'canberra_70m',
  DSS34: 'canberra_34m_1',
  DSS35: 'canberra_34m_2',
  DSS36: 'canberra_34m_3',
  DSS53: 'madrid_34m_1',
  DSS65: 'madrid_34m_2',
};

export type DsnBand = 'Ka' | 'X' | 'S' | '';

/** Band from a signal frequency in Hz: above 20 GHz Ka, above 7 GHz X, above 1 GHz S, otherwise none. */
export function bandFromFrequencyHz(freqHz: number): DsnBand {
  if (!Number.isFinite(freqHz)) return '';
  return freqHz > 20e9 ? 'Ka' : freqHz > 7e9 ? 'X' : freqHz > 1e9 ? 'S' : '';
}

/** True when the sim clock is an hour or more from real time (banner shown, no live lines). */
export function simTimeDiffers(simMs: number, realMs: number): boolean {
  return Math.abs(simMs - realMs) >= DSN_REAL_TIME_TOLERANCE_MS;
}

/** Backoff delay after the n-th consecutive failure (n >= 1), capped at the last step. */
export function backoffDelayMs(consecutiveFailures: number): number {
  const idx = Math.min(Math.max(consecutiveFailures, 1) - 1, DSN_BACKOFF_STEPS_MS.length - 1);
  return DSN_BACKOFF_STEPS_MS[idx];
}

export function isActiveSignal(s: DsnSignal): boolean {
  return s.active === 'true';
}

/** Dishes with a real target (not the DSN/DSS placeholders). */
export function activeDishes(station: DsnStation): DsnDish[] {
  return (station.dishes || []).filter((d) => {
    const target = d.targets?.[0];
    return Boolean(target) && target.name !== 'DSN' && target.name !== 'DSS';
  });
}

export function spacecraftInfo(code: string | undefined): SpacecraftInfo {
  if (code && SPACECRAFT_MAP[code]) return SPACECRAFT_MAP[code];
  return { id: null, name: code || '?', color: '#888' };
}

export function formatRangeKm(rangeKm: number): string {
  if (rangeKm > 1_000_000) return (rangeKm / 1_000_000).toFixed(1) + 'M km';
  if (rangeKm > 1000) return Math.round(rangeKm / 1000) + 'K km';
  return rangeKm + ' km';
}

export function formatRtlt(seconds: number): string {
  return seconds > 60 ? (seconds / 60).toFixed(1) + ' min' : seconds.toFixed(1) + ' s';
}

export function formatDataRate(bps: number): string {
  if (bps > 1_000_000) return (bps / 1_000_000).toFixed(1) + ' Mbps';
  if (bps > 1000) return (bps / 1000).toFixed(1) + ' kbps';
  return bps + ' bps';
}

export interface DishView {
  name: string;
  target: SpacecraftInfo;
  bands: DsnBand[];
  hasDownlink: boolean;
  hasUplink: boolean;
  dataRateText: string;
  uplinkPowerText: string;
  rangeText: string;
  rtltText: string;
  azDeg: string | null;
  elDeg: string | null;
  windKmh: string | null;
  downlinkGHz: string[];
  uplinkGHz: string[];
  hasActiveSignal: boolean;
}

/** The row model for one active dish, with the original's number formatting. */
export function dishView(dish: DsnDish): DishView {
  const target = dish.targets?.[0] ?? {};
  const activeDown = (dish.downSignals || []).filter(isActiveSignal);
  const activeUp = (dish.upSignals || []).filter(isActiveSignal);
  let dataRate = 0;
  for (const s of activeDown) dataRate += parseInt(String(s.dataRate), 10) || 0;
  const rangeKm = parseInt(String(target.downlegRange), 10) || 0;
  const rtlt = parseFloat(String(target.rtlt)) || 0;
  const bands: DsnBand[] = [];
  for (const s of activeDown.concat(activeUp)) {
    const band = bandFromFrequencyHz(parseFloat(String(s.frequency)) || 0);
    if (band && !bands.includes(band)) bands.push(band);
  }
  let uplinkPowerText = '';
  for (const s of activeUp) if (s.power) uplinkPowerText = parseFloat(s.power).toFixed(1) + ' kW';
  const ghz = (signals: DsnSignal[]) =>
    signals.filter((s) => s.frequency).map((s) => (parseFloat(String(s.frequency)) / 1e9).toFixed(3));
  return {
    name: dish.name || '?',
    target: spacecraftInfo(target.name),
    bands,
    hasDownlink: activeDown.length > 0,
    hasUplink: activeUp.length > 0,
    dataRateText: formatDataRate(dataRate),
    uplinkPowerText,
    rangeText: formatRangeKm(rangeKm),
    rtltText: formatRtlt(rtlt),
    azDeg: dish.azimuthAngle ? parseFloat(dish.azimuthAngle).toFixed(1) : null,
    elDeg: dish.elevationAngle ? parseFloat(dish.elevationAngle).toFixed(1) : null,
    windKmh: dish.windSpeed ? parseFloat(dish.windSpeed).toFixed(0) : null,
    downlinkGHz: ghz(activeDown),
    uplinkGHz: ghz(activeUp),
    hasActiveSignal: activeDown.length > 0 || activeUp.length > 0,
  };
}

export interface ContactPair {
  missionId: string;
  antennaId: string;
}

/**
 * Live contact pairs: dishes with a mapped spacecraft and a mapped antenna,
 * restricted to missions and antennas in the loaded dataset (the original
 * required both globe entities to exist). Pairs are unique.
 */
export function dsnContactPairs(
  data: DsnNowData | null,
  knownMissionIds: ReadonlySet<string>,
  knownAntennaIds: ReadonlySet<string>,
): ContactPair[] {
  if (!data) return [];
  const seen = new Set<string>();
  const pairs: ContactPair[] = [];
  for (const station of data.stations || []) {
    for (const dish of station.dishes || []) {
      const target = dish.targets?.[0];
      if (!target || target.name === 'DSN' || target.name === 'DSS') continue;
      const sc = target.name ? SPACECRAFT_MAP[target.name] : undefined;
      if (!sc || !sc.id) continue;
      const antennaId = dish.name ? DSN_DISH_MAP[dish.name] : undefined;
      if (!antennaId) continue;
      if (!knownMissionIds.has(sc.id) || !knownAntennaIds.has(antennaId)) continue;
      const key = `${sc.id}|${antennaId}`;
      if (seen.has(key)) continue;
      seen.add(key);
      pairs.push({ missionId: sc.id, antennaId });
    }
  }
  return pairs;
}

/** Union of the store's live contacts and the DSN pairs, de-duplicated. */
export function mergeContactPairs(base: readonly ContactPair[], extra: readonly ContactPair[]): ContactPair[] {
  const seen = new Set<string>();
  const out: ContactPair[] = [];
  for (const p of [...base, ...extra]) {
    const key = `${p.missionId}|${p.antennaId}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ missionId: p.missionId, antennaId: p.antennaId });
  }
  return out;
}

/**
 * Poll DSN Now every 10 s; after a failure switch to one-shot backoff timers
 * (30 s, 1 min, 2 min, 5 min cap) and resume the 10 s interval on recovery.
 */
export function startDsn(fetchData: () => Promise<DsnNowData> = () => loaderApi.dsnNow<DsnNowData>()): () => void {
  let active = true;
  let interval: ReturnType<typeof setInterval> | null = null;
  let backoff: ReturnType<typeof setTimeout> | null = null;
  let consecutiveFailures = 0;

  const resumeNormalPolling = () => {
    if (interval || !active) return;
    interval = setInterval(() => void poll(), DSN_POLL_MS);
  };

  const scheduleBackoff = () => {
    if (!active) return;
    if (interval) {
      clearInterval(interval);
      interval = null;
    }
    const delay = backoffDelayMs(consecutiveFailures);
    if (backoff) clearTimeout(backoff);
    useAwareness.getState().setDsn({ backoffMs: delay });
    backoff = setTimeout(() => {
      backoff = null;
      if (active) void poll();
    }, delay);
  };

  const poll = async () => {
    try {
      const data = await fetchData();
      if (!active) return;
      if (consecutiveFailures > 0) console.info(`[dsn] DSN Now feed recovered after ${consecutiveFailures} failures.`);
      consecutiveFailures = 0;
      useAwareness.getState().setDsn({ data, consecutiveFailures: 0, backoffMs: null, lastError: null, updatedMs: Date.now() });
      resumeNormalPolling();
    } catch (err) {
      if (!active) return;
      consecutiveFailures++;
      if (consecutiveFailures === 1) console.warn('[dsn] DSN Now feed unavailable; backing off and retrying:', err);
      useAwareness.getState().setDsn({ consecutiveFailures, lastError: errorMessage(err) });
      scheduleBackoff();
    }
  };

  useAwareness.getState().resetDsn();
  void poll();
  interval = setInterval(() => void poll(), DSN_POLL_MS);

  return () => {
    active = false;
    if (interval) clearInterval(interval);
    if (backoff) clearTimeout(backoff);
    useAwareness.getState().resetDsn();
  };
}
